/**
 * Générateur — Dans le plan muni d'un repère orthogonal : lire les coordonnées
 * d'un point donné ; placer un point de coordonnées données.
 * Attendu ID : 945 | Niveau : 5ème | Domaine : Espace et géométrie / Repérage
 *
 * Plan de cartes (version alignée sur la spécification 945 du 2026-09-21) :
 *
 *   Carte « Lire les coordonnées » — repère orthogonal gradué, trois points
 *     dont un sur chaque axe ; deux points interrogés, deux cases par point
 *     (abscisse puis ordonnée), conformément à `type_reponse`.
 *     Formats : entiers (niveaux 1 et 2), mélange (niveau 6), unités inégales
 *     (niveau 5), QCM du couple, QCM de l'écriture du couple.
 *
 *   Carte « Lecture précise (décimaux) » — mêmes figures au demi (niveau 3)
 *     et au quart (niveau 4) : la graduation secondaire devient nécessaire.
 *     Formats : saisie à trous, QCM.
 *
 *   Carte « Placer des points dans le repère » — placer_points_2d, trois
 *     points dont un sur chaque axe ; entiers (niveaux 1 et 2) puis demis et
 *     quarts (niveaux 3 et 4). Le point fantôme n'affiche pas ses
 *     coordonnées (`masquerCoordonnees`, décision prof 2026-09-20) : les lire
 *     EST le savoir évalué. Variante QCM : quelle croix marque (a ; b) ?
 *
 * ── Spécification 945 ──────────────────────────────────────────────────────
 *
 * Bornes appliquées (toutes issues de `variables`) :
 *   borne_inferieure_des_axes  -10 .. 0  → -1 (niveau 1, marge sous les axes),
 *                                          -7 .. -5 (plan complet), -10 (x du niveau 5)
 *   borne_superieure_des_axes    5 .. 10 → 5 .. 8
 *   pas_de_lecture_des_coordonnees 0,25 .. 1 → 1 (n1, n2, n5), 0,5 (n3), 0,25 (n4)
 *   rapport_des_unites_des_deux_axes 1 .. 2 → 1, et 2 au niveau 5
 *   abscisse / ordonnee_du_point -10 .. 10, multiples exacts du pas affiché
 *   nombre_de_points_par_repere  1 .. 5 → 3 (défaut de la spec)
 *   nombre_de_points_situes_sur_un_axe 1 .. 2 → 2 (un par axe, dès 3 points)
 *   tolerance_de_placement 0,25 .. 0,5 du pas → 0,5 (aimantation du composant)
 *
 * Exclusions appliquées :
 *   1. l'origine n'est jamais un point à lire ni à placer (coordonnée nulle
 *      seulement sur le point porté par un axe) ;
 *   2. deux points ne partagent ni abscisse ni ordonnée ;
 *   3. aucun point sur le cadre de la fenêtre (marge d'un pas de chaque côté) ;
 *   4. lettres O, Q et I écartées du nommage (NOMS_POINTS) ;
 *   5. toute coordonnée est un multiple exact du pas affiché ;
 *   6. repère toujours gradué, axes fléchés et nommés, origine nommée O.
 *
 * Erreurs visées → mots-clés (code des distracteurs / `erreursVisees`) :
 *   Intervertir abscisse et ordonnée                      → inversion_couple
 *   Deux coordonnées non nulles sur un point d'un axe,
 *     ou (0 ; 0) dès qu'une coordonnée est nulle          → coordonnee_nulle_ignoree
 *   Lire la graduation secondaire comme une unité         → graduation_secondaire_lue_unite
 *   Compter les carreaux au lieu de lire les étiquettes   → carreaux_comptes
 *   Oublier le signe d'une coordonnée près d'un axe       → signe_oublie
 *   Écrire le couple sans séparateur ou dans un ordre
 *     non conventionnel                                   → ecriture_du_couple
 *
 * Graduation → cartes :
 *   niveau 1 (quart de plan, entiers)        lire_entiers, placer, mélange
 *   niveau 2 (plan complet, entiers)         lire_entiers, placer, lire_qcm, mélange
 *   niveau 3 (demis)                         precis_*, placer_precis, mélange
 *   niveau 4 (quarts)                        precis_*, placer_precis, mélange
 *   niveau 5 (unités inégales)               lire_unites, lire_qcm, mélange
 *   niveau 6 (mélange)                       lire_texte_trou_saisie, lire_qcm_unique
 *
 * Points non tranchés (détail dans audit/5eme/945.md) : l'aimantation du
 * composant ne se désactive pas (réglage transverse) ; le repère interactif
 * rend x et y à des échelles graphiques différentes par construction, les
 * unités inégales sont donc portées par les figures SVG.
 *
 * ⚠ Croix lettrées du QCM : les choix sont EXACTEMENT ['A','B','C','D'] et la
 *   bonne réponse est la lettre elle-même (idiome gen_1114).
 * Arithmétique interne en QUARTS entiers (zéro flottant).
 * Convention d'écriture : tiret clavier « - », jamais U+2212, jamais de « + ».
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, basePayload, pickLettresSains,
} from '../../../utils';

const ATTENDU_ID = 945;

const L_LIRE = 'Lire les coordonnées';
const L_PRECIS = 'Lecture précise (décimaux)';
const L_PLACER = 'Placer des points dans le repère';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Arithmétique en quarts d'unité (entiers) ────────────────────────────────

/** quarts → écriture française canonique ("-1,5", "0,25", "3", "0"). */
const frQ = (q) => {
    const s = q < 0 ? '-' : '';
    const a = Math.abs(q);
    const e = Math.floor(a / 4);
    const r = a % 4;
    const dec = r === 1 ? '25' : (r === 2 ? '5' : '75');
    return s + (r === 0 ? String(e) : `${e},${dec}`);
};
const paire = (xQ, yQ) => `(${frQ(xQ)} ; ${frQ(yQ)})`;
/** Arrondi à l'unité, moitiés éloignées de zéro (erreur « graduation secondaire »). */
const arrondiUnite = (q) => (q < 0 ? -1 : 1) * Math.round(Math.abs(q) / 4) * 4;

// Exclusion 4 : ni O (origine), ni Q, ni I (confusion manuscrite avec un chiffre).
const NOMS_POINTS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'P', 'R', 'S', 'T'];

const CONSIGNE_ECRITURE = '(Écris chaque nombre sans le signe + : 3 ou -2,5)';

// ─── Régimes = niveaux de la graduation de difficulté ────────────────────────
// Toutes les bornes sont en QUARTS. `rapportY` = rapport des unités des deux axes.

const REGIMES = {
    // niveau 1 : quart de plan positif, entiers. La fenêtre déborde d'une unité
    // sous les axes pour que les points d'abscisse (ou d'ordonnée) nulle ne
    // soient pas posés sur le cadre (exclusion 3).
    n1: () => { const b = randInt(5, 8); return { nom: 'n1', xMinQ: -4, xMaxQ: 4 * b, yMinQ: -4, yMaxQ: 4 * b, pasQ: 4, rapportY: 1, positif: true }; },
    // niveau 2 : plan complet, entiers relatifs
    n2: () => { const b = randInt(5, 7); return { nom: 'n2', xMinQ: -4 * b, xMaxQ: 4 * b, yMinQ: -4 * b, yMaxQ: 4 * b, pasQ: 4, rapportY: 1, positif: false }; },
    // niveau 3 : plan complet, coordonnées en demis
    n3: () => { const b = randInt(5, 6); return { nom: 'n3', xMinQ: -4 * b, xMaxQ: 4 * b, yMinQ: -4 * b, yMaxQ: 4 * b, pasQ: 2, rapportY: 1, positif: false }; },
    // niveau 4 : plan complet, coordonnées en quarts
    n4: () => ({ nom: 'n4', xMinQ: -20, xMaxQ: 20, yMinQ: -20, yMaxQ: 20, pasQ: 1, rapportY: 1, positif: false }),
    // niveau 5 : unités différentes sur les deux axes (l'unité des ordonnées
    // vaut le double de celle des abscisses), coordonnées entières
    n5: () => ({ nom: 'n5', xMinQ: -40, xMaxQ: 40, yMinQ: -20, yMaxQ: 20, pasQ: 4, rapportY: 2, positif: false }),
};

const tirerRegime = (noms) => REGIMES[randomChoice(noms)]();

/** Valeurs admissibles sur un axe : multiples du pas, hors cadre et hors zéro. */
const valeursAxe = (reg, axe) => {
    const minQ = axe === 'x' ? reg.xMinQ : reg.yMinQ;
    const maxQ = axe === 'x' ? reg.xMaxQ : reg.yMaxQ;
    const out = [];
    for (let v = minQ + reg.pasQ; v <= maxQ - reg.pasQ; v += reg.pasQ) {
        if (v === 0) continue;                                  // exclusion 1
        if (reg.positif && v < 0) continue;                     // niveau 1 : quart de plan
        if (reg.pasQ === 2 && Math.abs(v) % 4 !== 2) continue;  // niveau 3 : demis francs
        if (reg.pasQ === 1 && Math.abs(v) % 2 !== 1) continue;  // niveau 4 : quarts francs
        out.push(v);
    }
    return out;
};

/**
 * Trois points : (u ; 0) sur l'axe des abscisses, (0 ; v) sur l'axe des
 * ordonnées, (p ; q) dans un quadrant. Abscisses deux à deux distinctes,
 * ordonnées aussi (exclusion 2) ; |p| ≠ |q| (point de vigilance 6).
 * `cond(p, q, u, v)` ajoute la contrainte du format ; repli déterministe.
 */
const tirerFigure = (reg, cond) => {
    const vx = valeursAxe(reg, 'x');
    const vy = valeursAxe(reg, 'y');
    const ok = (p, q, u, v) => p !== u && q !== v && Math.abs(p) !== Math.abs(q);
    let choisi = null;
    for (let essai = 0; essai < 150 && !choisi; essai++) {
        const u = randomChoice(vx), v = randomChoice(vy);
        const p = randomChoice(vx), q = randomChoice(vy);
        if (ok(p, q, u, v) && (!cond || cond(p, q, u, v))) choisi = { u, v, p, q };
    }
    if (!choisi) {                                              // repli déterministe borné
        for (const u of vx) for (const v of vy) for (const p of vx) for (const q of vy) {
            if (!choisi && ok(p, q, u, v) && (!cond || cond(p, q, u, v))) choisi = { u, v, p, q };
        }
    }
    if (!choisi) {                                              // repli sans la contrainte du format
        for (const u of vx) for (const v of vy) for (const p of vx) for (const q of vy) {
            if (!choisi && ok(p, q, u, v)) choisi = { u, v, p, q };
        }
    }
    const pts = shuffle([
        { xQ: choisi.u, yQ: 0, role: 'axeX' },
        { xQ: 0, yQ: choisi.v, role: 'axeY' },
        { xQ: choisi.p, yQ: choisi.q, role: 'quadrant' },
    ]);
    const lettres = pickLettresSains(NOMS_POINTS, pts.length);
    pts.forEach((pt, i) => { pt.label = lettres[i]; });
    const parRole = (r) => pts.find((pt) => pt.role === r);
    return { reg, pts, axeX: parRole('axeX'), axeY: parRole('axeY'), quadrant: parRole('quadrant') };
};

/** Traçabilité pour le harnais et le test de spécification (valeurs en unités). */
const paramsFig = (reg, pts) => ({
    niveau: reg.nom,
    repere: {
        xMin: reg.xMinQ / 4, xMax: reg.xMaxQ / 4,
        yMin: reg.yMinQ / 4, yMax: reg.yMaxQ / 4,
        pas: reg.pasQ / 4, rapport: reg.rapportY,
    },
    points: pts.map((p) => [p.xQ / 4, p.yQ / 4]),
    lettres: pts.map((p) => p.label),
});

// ─── Figure SVG du repère ────────────────────────────────────────────────────

/**
 * Repère orthogonal gradué : quadrillage au pas de lecture, axes fléchés et
 * nommés, origine nommée O, graduations principales étiquetées sur les deux
 * axes, points marqués par une croix, étiquette décalée vers l'extérieur.
 * Au niveau 5, l'unité de l'axe des ordonnées est graphiquement DOUBLE de
 * celle des abscisses : le quadrillage n'est plus formé de carrés.
 */
const svgRepere = (reg, points) => {
    const nUx = (reg.xMaxQ - reg.xMinQ) / 4;
    const nUy = (reg.yMaxQ - reg.yMinQ) / 4;
    const u = Math.round(Math.min(440 / nUx, 440 / (nUy * reg.rapportY)) * 10) / 10;
    const uY = u * reg.rapportY;
    const plotW = nUx * u, plotH = nUy * uY;
    const padL = 36, padR = 26, padT = 26, padB = 34;
    const W = Math.round(padL + plotW + padR), H = Math.round(padT + plotH + padB);
    const px = (xQ) => Math.round((padL + ((xQ - reg.xMinQ) / 4) * u) * 10) / 10;
    const py = (yQ) => Math.round((padT + plotH - ((yQ - reg.yMinQ) / 4) * uY) * 10) / 10;
    const x0 = px(0), y0 = py(0);
    const pasEtiqX = nUx > 14 ? 2 : 1;
    const pasEtiqY = nUy > 14 ? 2 : 1;
    const out = [];
    out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" font-family="system-ui, sans-serif">`);
    out.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`);

    // Quadrillage secondaire (au pas de lecture) puis principal (à l'unité)
    if (reg.pasQ < 4) {
        for (let v = reg.xMinQ; v <= reg.xMaxQ; v += reg.pasQ) {
            if (v % 4 === 0) continue;
            out.push(`<line x1="${px(v)}" y1="${py(reg.yMinQ)}" x2="${px(v)}" y2="${py(reg.yMaxQ)}" stroke="#f1f5f9" stroke-width="1"/>`);
        }
        for (let v = reg.yMinQ; v <= reg.yMaxQ; v += reg.pasQ) {
            if (v % 4 === 0) continue;
            out.push(`<line x1="${px(reg.xMinQ)}" y1="${py(v)}" x2="${px(reg.xMaxQ)}" y2="${py(v)}" stroke="#f1f5f9" stroke-width="1"/>`);
        }
    }
    for (let v = Math.ceil(reg.xMinQ / 4) * 4; v <= reg.xMaxQ; v += 4) {
        if (v === 0) continue;
        out.push(`<line x1="${px(v)}" y1="${py(reg.yMinQ)}" x2="${px(v)}" y2="${py(reg.yMaxQ)}" stroke="#e2e8f0" stroke-width="1"/>`);
    }
    for (let v = Math.ceil(reg.yMinQ / 4) * 4; v <= reg.yMaxQ; v += 4) {
        if (v === 0) continue;
        out.push(`<line x1="${px(reg.xMinQ)}" y1="${py(v)}" x2="${px(reg.xMaxQ)}" y2="${py(v)}" stroke="#e2e8f0" stroke-width="1"/>`);
    }

    // Axes fléchés et nommés
    out.push(`<line x1="${px(reg.xMinQ)}" y1="${y0}" x2="${padL + plotW + 12}" y2="${y0}" stroke="#334155" stroke-width="1.6"/>`);
    out.push(`<polygon points="${padL + plotW + 18},${y0} ${padL + plotW + 9},${y0 - 4} ${padL + plotW + 9},${y0 + 4}" fill="#334155"/>`);
    out.push(`<text x="${padL + plotW + 14}" y="${y0 + 19}" text-anchor="middle" font-size="13" font-style="italic" fill="#334155">x</text>`);
    out.push(`<line x1="${x0}" y1="${py(reg.yMinQ)}" x2="${x0}" y2="${padT - 12}" stroke="#334155" stroke-width="1.6"/>`);
    out.push(`<polygon points="${x0},${padT - 18} ${x0 - 4},${padT - 9} ${x0 + 4},${padT - 9}" fill="#334155"/>`);
    out.push(`<text x="${x0 + 13}" y="${padT - 12}" text-anchor="middle" font-size="13" font-style="italic" fill="#334155">y</text>`);

    // Graduations principales étiquetées sur les deux axes
    for (let v = Math.ceil(reg.xMinQ / 4) * 4; v <= reg.xMaxQ; v += 4) {
        if (v === 0) continue;
        out.push(`<line x1="${px(v)}" y1="${y0 - 4}" x2="${px(v)}" y2="${y0 + 4}" stroke="#334155" stroke-width="1"/>`);
        if ((v / 4) % pasEtiqX === 0) out.push(`<text x="${px(v)}" y="${y0 + 18}" text-anchor="middle" font-size="12" fill="#1e293b">${frQ(v)}</text>`);
    }
    for (let v = Math.ceil(reg.yMinQ / 4) * 4; v <= reg.yMaxQ; v += 4) {
        if (v === 0) continue;
        out.push(`<line x1="${x0 - 4}" y1="${py(v)}" x2="${x0 + 4}" y2="${py(v)}" stroke="#334155" stroke-width="1"/>`);
        if ((v / 4) % pasEtiqY === 0) out.push(`<text x="${x0 - 8}" y="${py(v)}" text-anchor="end" dominant-baseline="middle" font-size="12" fill="#1e293b">${frQ(v)}</text>`);
    }
    out.push(`<text x="${x0 - 8}" y="${y0 + 17}" text-anchor="end" font-size="13" fill="#1e293b">O</text>`);

    // Points : croix + lettre majuscule décalée vers l'extérieur
    points.forEach((p) => {
        const cx = px(p.xQ), cy = py(p.yQ);
        out.push('<g class="pt">');
        out.push(`<line x1="${cx - 5}" y1="${cy - 5}" x2="${cx + 5}" y2="${cy + 5}" stroke="#1e3a8a" stroke-width="2"/>`);
        out.push(`<line x1="${cx - 5}" y1="${cy + 5}" x2="${cx + 5}" y2="${cy - 5}" stroke="#1e3a8a" stroke-width="2"/>`);
        const dx = p.xQ < 0 ? -9 : 9;
        const ancre = p.xQ < 0 ? 'end' : 'start';
        const dy = p.yQ < 0 ? 19 : -9;
        out.push(`<text x="${cx + dx}" y="${cy + dy}" text-anchor="${ancre}" font-size="15" font-weight="700" fill="#1e3a8a">${p.label}</text>`);
        out.push('</g>');
    });

    out.push('</svg>');
    return out.join('');
};

const figure = (reg, points) => ({
    version: 1,
    elements: [{
        type: 'image',
        config: {
            imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svgRepere(reg, points)),
            maxWidth: 'medium',
            alt: 'Repère orthogonal gradué',
        },
    }],
});

// ─── Carte « Lire les coordonnées » et « Lecture précise (décimaux) » ────────

/** Deux points interrogés : celui d'un axe (coordonnée nulle) et celui d'un quadrant. */
const genLectureSaisie = (labelCarte, noms, erreursVisees) => () => {
    const reg = tirerRegime(noms);
    const t = tirerFigure(reg);
    const interroges = shuffle([randomChoice([t.axeX, t.axeY]), t.quadrant]);
    const phrase = interroges
        .map((p) => `${p.label} : abscisse {{${frQ(p.xQ)}}} ; ordonnée {{${frQ(p.yQ)}}}.`)
        .join('\n');
    const payload = makeTexteTrou(phrase,
        `<p><b>Lis les coordonnées des points demandés.</b> ${CONSIGNE_ECRITURE}</p>`,
        { ...opts(labelCarte), mode: 'saisie', elements_additionnels: figure(reg, t.pts) });
    payload._meta.params = {
        ...paramsFig(reg, t.pts),
        interroges: interroges.map((p) => [p.xQ / 4, p.yQ / 4]),
        erreursVisees,
    };
    return payload;
};

/**
 * QCM du couple. La cible et les trois distracteurs sont fixés par le régime,
 * chacun codé sur une erreur visée ; jamais de distracteur bouche-trou.
 */
const construireLectureQcm = (labelCarte, noms) => {
    const reg = tirerRegime(noms);
    let t, cible, distracteurs;
    for (let essai = 0; essai < 60; essai++) {
        if (reg.nom === 'n2') {
            // cible sur un axe, coordonnée non nulle négative
            t = tirerFigure(reg, (p, q, u, v) => u < 0 || v < 0);
            cible = [t.axeX, t.axeY].find((p) => p.xQ < 0 || p.yQ < 0);
            if (!cible) continue;
            distracteurs = [
                { code: 'inversion_couple', valeur: paire(cible.yQ, cible.xQ) },
                { code: 'coordonnee_nulle_ignoree', valeur: paire(0, 0) },
                { code: 'signe_oublie', valeur: paire(Math.abs(cible.xQ), Math.abs(cible.yQ)) },
            ];
        } else if (reg.nom === 'n5') {
            t = tirerFigure(reg, (p, q) => p < 0 || q < 0);
            cible = t.quadrant;
            distracteurs = [
                { code: 'inversion_couple', valeur: paire(cible.yQ, cible.xQ) },
                // l'unité des ordonnées vaut le double : mesurer la hauteur avec
                // l'unité des abscisses (compter les carreaux) double l'ordonnée
                { code: 'carreaux_comptes', valeur: paire(cible.xQ, 2 * cible.yQ) },
                { code: 'signe_oublie', valeur: paire(Math.abs(cible.xQ), Math.abs(cible.yQ)) },
            ];
        } else {
            t = tirerFigure(reg, (p, q) => p < 0 || q < 0);
            cible = t.quadrant;
            distracteurs = [
                { code: 'inversion_couple', valeur: paire(cible.yQ, cible.xQ) },
                { code: 'graduation_secondaire_lue_unite', valeur: paire(arrondiUnite(cible.xQ), arrondiUnite(cible.yQ)) },
                { code: 'signe_oublie', valeur: paire(Math.abs(cible.xQ), Math.abs(cible.yQ)) },
            ];
        }
        const correct = paire(cible.xQ, cible.yQ);
        const vus = new Set([correct]);
        if (distracteurs.every((d) => { const neuf = !vus.has(d.valeur); vus.add(d.valeur); return neuf; })) {
            const payload = makeQcm([correct, ...distracteurs.map((d) => d.valeur)], correct,
                `<p><b>Quelles sont les coordonnées du point ${cible.label} ?</b></p>`,
                { ...opts(labelCarte), elements_additionnels: figure(reg, t.pts) });
            payload._meta.params = { ...paramsFig(reg, t.pts), cible: [cible.xQ / 4, cible.yQ / 4], distracteurs };
            return payload;
        }
    }
    return null;
};

const genLectureQcm = (labelCarte, noms) => () => {
    for (let essai = 0; essai < 40; essai++) {
        const p = construireLectureQcm(labelCarte, noms);
        if (p) return p;
    }
    // repli déterministe : régime entier, cible sur l'axe des abscisses
    const reg = REGIMES.n2();
    const cible = { xQ: -8, yQ: 0, label: 'A' };
    const pts = [cible, { xQ: 0, yQ: 12, label: 'B' }, { xQ: 4, yQ: -8, label: 'C' }];
    const distracteurs = [
        { code: 'inversion_couple', valeur: paire(0, -8) },
        { code: 'coordonnee_nulle_ignoree', valeur: paire(0, 0) },
        { code: 'signe_oublie', valeur: paire(8, 0) },
    ];
    const correct = paire(cible.xQ, cible.yQ);
    const payload = makeQcm([correct, ...distracteurs.map((d) => d.valeur)], correct,
        `<p><b>Quelles sont les coordonnées du point ${cible.label} ?</b></p>`,
        { ...opts(labelCarte), elements_additionnels: figure(reg, pts) });
    payload._meta.params = { ...paramsFig(reg, pts), cible: [cible.xQ / 4, cible.yQ / 4], distracteurs };
    return payload;
};

/** QCM de l'écriture du couple : séparateur et ordre (erreur visée 6). */
const genEcritureQcm = () => {
    const reg = tirerRegime(['n1', 'n2', 'n3', 'n4', 'n5']);
    const t = tirerFigure(reg);
    const c = t.quadrant;
    const correct = paire(c.xQ, c.yQ);
    const distracteurs = [
        { code: 'ecriture_du_couple', valeur: `(${frQ(c.xQ)} ${frQ(c.yQ)})` },
        { code: 'inversion_couple', valeur: paire(c.yQ, c.xQ) },
        { code: 'ecriture_du_couple', valeur: `(${frQ(c.yQ)} ${frQ(c.xQ)})` },
    ];
    const payload = makeQcm([correct, ...distracteurs.map((d) => d.valeur)], correct,
        `<p><b>Le point ${c.label} est placé dans le repère. Quelle écriture donne correctement ses coordonnées ?</b></p>`,
        { ...opts(L_LIRE), elements_additionnels: figure(reg, t.pts) });
    payload._meta.params = { ...paramsFig(reg, t.pts), cible: [c.xQ / 4, c.yQ / 4], distracteurs };
    return payload;
};

// ─── Carte « Placer des points dans le repère » ──────────────────────────────

const genPlacer = (noms, erreursVisees) => () => {
    const reg = tirerRegime(noms);
    const t = tirerFigure(reg);
    const bonne = {};
    t.pts.forEach((p) => { bonne[p.label] = `${frQ(p.xQ)};${frQ(p.yQ)}`; });
    const liste = t.pts.map((p) => `${p.label}${paire(p.xQ, p.yQ)}`);
    const enonce = `${liste.slice(0, -1).join(', ')} et ${liste[liste.length - 1]}`;
    const payload = basePayload('association',
        `<p><b>Place les points ${enonce} dans le repère.</b></p>`,
        {
            subType: 'placer_points_2d',
            repere: {
                xMin: reg.xMinQ / 4, yMin: reg.yMinQ / 4,
                xMax: reg.xMaxQ / 4, yMax: reg.yMaxQ / 4,
                xStep: reg.pasQ / 4, yStep: reg.pasQ / 4,
                xLabel: 'x', yLabel: 'y',
            },
            pointsToPlace: t.pts.map((p) => p.label),
            dejaPlaces: [],
            bonne_reponse: JSON.stringify(bonne),
            // Le point fantôme n'affiche pas ses coordonnées : les lire EST le
            // savoir évalué (décision prof 2026-09-20, FORMATS_TANOED §9bis).
            masquerCoordonnees: true,
        },
        opts(L_PLACER));
    payload._meta.params = {
        ...paramsFig(reg, t.pts),
        erreursVisees,
    };
    return payload;
};

/**
 * Quelle croix marque (a ; b) ? Cinq croix, en nombre CONSTANT :
 *   (a ; b) cible, (b ; a) ordre inversé, (-a ; -b) signes oubliés,
 *   (0 ; c) et (d ; 0) les deux points portés par un axe.
 * Leurs abscisses sont deux à deux distinctes, leurs ordonnées aussi
 * (exclusion 2) ; |a| ≠ |b| les rend toutes discernables (vigilance 6).
 */
const genCroixQcm = () => {
    const reg = tirerRegime(['n2', 'n3']);
    const vx = valeursAxe(reg, 'x');
    const vy = valeursAxe(reg, 'y');
    let tir = null;
    for (let essai = 0; essai < 200 && !tir; essai++) {
        const a = randomChoice(vx), b = randomChoice(vy);
        if (Math.abs(a) === Math.abs(b)) continue;
        if (!vx.includes(-a) || !vy.includes(-b)) continue;
        const c = randomChoice(vy), d = randomChoice(vx);
        if ([b, a, -b].includes(c) || [a, b, -a].includes(d)) continue;
        tir = { a, b, c, d };
    }
    if (!tir) {                                     // repli déterministe borné
        for (const a of vx) for (const b of vy) for (const c of vy) for (const d of vx) {
            if (!tir && Math.abs(a) !== Math.abs(b) && vx.includes(-a) && vy.includes(-b)
                && ![b, a, -b].includes(c) && ![a, b, -a].includes(d)) tir = { a, b, c, d };
        }
    }
    const { a, b, c, d } = tir;
    const croix = [[a, b], [b, a], [-a, -b], [0, c], [d, 0]];
    const codes = ['cible', 'inversion_couple', 'signe_oublie', 'coordonnee_nulle_ignoree', 'coordonnee_nulle_ignoree'];
    const ordre = shuffle([0, 1, 2, 3, 4]);
    const lettres = ['A', 'B', 'C', 'D', 'E'];
    const points = ordre.map((k, i) => ({ xQ: croix[k][0], yQ: croix[k][1], label: lettres[i], code: codes[k] }));
    const correct = points.find((p) => p.code === 'cible').label;
    const payload = makeQcm([...lettres], correct,
        `<p><b>Quelle croix marque le point de coordonnées ${paire(a, b)} ?</b></p>`,
        { ...opts(L_PLACER), elements_additionnels: figure(reg, points) });
    // Choix = les lettres dans l'ordre, la bonne réponse est la lettre elle-même
    // (idiome gen_1114 : une lettre seule serait sinon relue comme un index).
    payload.contenu.choix = [...lettres];
    payload.contenu.bonne_reponse = correct;
    payload._meta.params = {
        ...paramsFig(reg, points),
        cible: [a / 4, b / 4],
        distracteurs: points.filter((p) => p.code !== 'cible').map((p) => ({ code: p.code, valeur: `${p.label}${paire(p.xQ, p.yQ)}` })),
    };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────

const GENERATORS = {
    lire_entiers_texte_trou_saisie: genLectureSaisie(L_LIRE, ['n1', 'n2'], ['coordonnee_nulle_ignoree', 'inversion_couple']),
    lire_texte_trou_saisie: genLectureSaisie(L_LIRE, ['n1', 'n2', 'n3', 'n4', 'n5'], ['inversion_couple', 'coordonnee_nulle_ignoree']),
    lire_unites_texte_trou_saisie: genLectureSaisie(L_LIRE, ['n5'], ['carreaux_comptes', 'inversion_couple']),
    lire_qcm_unique: genLectureQcm(L_LIRE, ['n2', 'n3', 'n4', 'n5']),
    lire_ecriture_qcm_unique: genEcritureQcm,
    precis_texte_trou_saisie: genLectureSaisie(L_PRECIS, ['n3', 'n4'], ['graduation_secondaire_lue_unite', 'inversion_couple']),
    precis_qcm_unique: genLectureQcm(L_PRECIS, ['n3', 'n4']),
    placer_placer_points_2d: genPlacer(['n1', 'n2'], ['coordonnee_nulle_ignoree', 'inversion_couple']),
    placer_precis_placer_points_2d: genPlacer(['n3', 'n4'], ['graduation_secondaire_lue_unite', 'inversion_couple']),
    placer_croix_qcm_unique: genCroixQcm,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous',
    entiers_texte_trou_saisie: 'Saisie trous (entiers)',
    unites_texte_trou_saisie: 'Saisie trous (unités inégales)',
    qcm_unique: 'QCM',
    ecriture_qcm_unique: 'QCM écriture',
    placer_points_2d: 'Placer (interactif)',
    precis_placer_points_2d: 'Placer (demis et quarts)',
    croix_qcm_unique: 'QCM croix',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lire', label: L_LIRE, formatsList: buildFormats('lire') },
    { id: 'precis', label: L_PRECIS, formatsList: buildFormats('precis') },
    { id: 'placer', label: L_PLACER, formatsList: buildFormats('placer') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
